import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { request } from "../api";
import { login as goToHubLogin, logout as clearAndLeave } from "../login";
import { nextAction } from "./session";
import type { SessionUser } from "./session";

type Status = "loading" | "authenticated" | "denied" | "unreachable";

interface AuthValue {
  status: Status;
  user: SessionUser | null;
  /** The role for this app. Undefined until authenticated. */
  appRole: string | undefined;
  login: () => void;
  logout: () => void;
  reload: () => void;
}

const AuthContext = createContext<AuthValue | null>(null);

interface MeResponse {
  user: SessionUser | null;
}

/**
 * Owns the session for the whole tree.
 *
 * It does not re-implement any of the auth rules — request() already handles
 * the single-flight refresh, the retry, and the NO_APP_PERMISSION redirect to
 * the Hub's modal. This only maps the outcome to something the UI can render,
 * via nextAction().
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [user, setUser] = useState<SessionUser | null>(null);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    const res = await request<MeResponse>("/api/me");
    const action = nextAction(res.status, Boolean(res.data?.user));

    if (action === "retry") {
      // The server said nothing. Say nothing about the session either, and
      // come back — do not bounce the user to the Hub mid-deploy.
      setStatus("unreachable");
      retryTimer.current = setTimeout(() => void load(), 3000);
      return;
    }

    if (action === "denied") {
      // request() is already navigating to the Hub's modal. Render a holding
      // state; a second redirect from here is the loop.
      setStatus("denied");
      return;
    }

    // "render" implies a user, but narrow it rather than assert: a server that
    // returns 200 with no user must send them to the Hub, not crash the tree.
    const nextUser = res.data?.user ?? null;
    if (action === "login" || !nextUser) {
      goToHubLogin();
      return;
    }

    setUser(nextUser);
    setStatus("authenticated");
  }, []);

  useEffect(() => {
    void load();
    return () => {
      if (retryTimer.current) clearTimeout(retryTimer.current);
    };
  }, [load]);

  const value = useMemo<AuthValue>(
    () => ({
      status,
      user,
      appRole: user?.appRole,
      login: goToHubLogin,
      logout: () => void clearAndLeave(),
      reload: () => void load(),
    }),
    [status, user, load],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
